import { supabase } from '../lib/supabase'
import { getCurrentUserId, verifyPropertyWriteAccess } from '../lib/auth'
import { applyPropertyFilter } from '../lib/portfolioFilter'
import type { Property, PropertyInsert, PropertyUpdate } from '../types/property'
import { setupXeroTracking } from './xero/tracking'

export async function getProperties(userId?: string, includeArchived = false, propertyIds?: string[]): Promise<Property[]> {
  let query = supabase
    .from('properties')
    .select('*')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  // When an explicit userId is passed (e.g. effectiveOwnerId for team members),
  // filter by owner. When omitted, RLS scopes: owners see own properties,
  // team members see accessible properties via get_team_accessible_property_ids.
  if (userId) {
    query = query.eq('owner_id', userId)
  }

  query = applyPropertyFilter(query, propertyIds, 'id')

  const { data, error } = await query
  if (error) throw error

  // Filter archived properties client-side (handles both pre and post migration)
  if (includeArchived) {
    return data.filter(p => p.status === 'archived')
  }

  return data.filter(p => !p.status || p.status === 'active')
}

export async function getProperty(id: string, userId?: string): Promise<Property> {
  const resolvedUserId = userId ?? await getCurrentUserId()
  const { data, error } = await supabase
    .from('properties')
    .select('*')
    .eq('id', id)
    .eq('owner_id', resolvedUserId)
    .is('deleted_at', null)
    .single()

  if (error) throw error
  return data
}

export async function createProperty(input: PropertyInsert, agentId: string, portfolioId?: string): Promise<Property> {
  const { data, error } = await supabase.rpc('create_property_with_portfolio', {
    p_property: input as unknown as Record<string, unknown>,
    p_agent_id: agentId,
    p_portfolio_id: portfolioId ?? null,
  })

  if (error) throw error
  if (!data) throw new Error('Property creation returned no data')

  const property = data as unknown as Property

  // If Xero is connected, sync tracking for the new property.
  // Idempotent — skips properties that already have tracking options.
  try {
    const { data: conn } = await supabase
      .from('xero_connections')
      .select('is_active')
      .eq('owner_id', agentId)
      .eq('is_active', true)
      .maybeSingle()

    if (conn) {
      setupXeroTracking().catch(() => {})
    }
  } catch {
    // Non-fatal: property was created, Xero sync is best-effort
  }

  return property
}

export async function updateProperty(id: string, input: PropertyUpdate): Promise<Property> {
  const userId = await getCurrentUserId()
  const { data, error } = await supabase
    .from('properties')
    .update(input)
    .eq('id', id)
    .eq('owner_id', userId)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function deleteProperty(id: string): Promise<void> {
  await verifyPropertyWriteAccess(id)
  const { error } = await supabase.rpc('soft_delete_record', {
    target_table: 'properties',
    record_id: id,
  })

  if (error) throw error
}

export async function archiveProperty(id: string): Promise<Property> {
  await verifyPropertyWriteAccess(id)
  const { data, error } = await supabase
    .from('properties')
    .update({ status: 'archived' })
    .eq('id', id)
    .is('deleted_at', null)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function restoreProperty(id: string): Promise<Property> {
  await verifyPropertyWriteAccess(id)
  const { data, error } = await supabase
    .from('properties')
    .update({ status: 'active' })
    .eq('id', id)
    .is('deleted_at', null)
    .select()
    .single()

  if (error) throw error
  return data
}

export interface PropertyActiveEntities {
  activeTenancies: number
  openIssues: number
}

export async function getPropertyActiveEntities(propertyId: string): Promise<PropertyActiveEntities> {
  await verifyPropertyWriteAccess(propertyId)

  // Check for active tenancies
  const { count: tenancyCount, error: tenancyError } = await supabase
    .from('tenancies')
    .select('*', { count: 'exact', head: true })
    .eq('property_id', propertyId)
    .is('deleted_at', null)
    .in('status', ['onboarding', 'moving_in', 'active', 'ending'])

  if (tenancyError) throw tenancyError

  // Check for open issues (not closed or cancelled)
  const { count: issueCount, error: issueError } = await supabase
    .from('issues')
    .select('*', { count: 'exact', head: true })
    .eq('property_id', propertyId)
    .not('status', 'in', '("closed","cancelled")')

  if (issueError) throw issueError

  return {
    activeTenancies: tenancyCount ?? 0,
    openIssues: issueCount ?? 0
  }
}
